import { X } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import type { ComponentPropsWithRef, ReactNode } from "react";

import { cx } from "@/lib/cx";

import { IconButton } from "./Button";
import styles from "./Dialog.module.css";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export interface DialogContentProps extends Omit<
  ComponentPropsWithRef<typeof DialogPrimitive.Content>,
  "title"
> {
  /** Every dialog is named by its title. */
  title: ReactNode;
  description?: ReactNode;
  size?: "md" | "lg";
  closeLabel?: string;
}

/** Modal dialog: focus is trapped, Escape and the close button dismiss, focus returns to the trigger. */
export function DialogContent({
  title,
  description,
  size = "md",
  closeLabel = "Close",
  className,
  children,
  ...props
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={cx(styles.overlay, "animate-in")} />
      <DialogPrimitive.Content
        {...(description === undefined ? { "aria-describedby": undefined } : {})}
        className={cx(styles.content, styles[size], "animate-in", className)}
        {...props}
      >
        <DialogPrimitive.Title className={styles.title}>{title}</DialogPrimitive.Title>
        {description !== undefined && (
          <DialogPrimitive.Description className={styles.description}>
            {description}
          </DialogPrimitive.Description>
        )}
        <div className={styles.body}>{children}</div>
        {/* Last in DOM order so initial focus lands on the first field, not on Close. */}
        <DialogPrimitive.Close asChild>
          <IconButton
            label={closeLabel}
            icon={<X />}
            size="sm"
            tooltip={false}
            className={styles.close}
          />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function DialogFooter({ className, ...props }: ComponentPropsWithRef<"div">) {
  return <div className={cx(styles.footer, className)} {...props} />;
}
